import { FC, useMemo, useState } from "react";
import { Calendar, DatePicker, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { Users, Truck } from "lucide-react";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { useGetServiceTickets } from "~/services/service-ticket";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { StaffAvailability, JobStatus, VesselStatus, ServiceType } from "~/utils/constants";

interface ServiceDatePickerProps {
  value?: Dayjs | null;
  onChange?: (date: Dayjs | null) => void;
  disabled?: boolean;
  className?: string;
  serviceType?: ServiceType;
}

interface DayStats {
  date: string;
  availableCount: number;
  partTimeCount: number;
  bookedCount: number;
  unavailableCount: number;
  totalWithSchedule: number;
  availableDrivers: number;
  availableLeads: number;
}

interface VesselDayStats {
  vesselId: string;
  vesselName: string;
  status: VesselStatus;
  totalHours: number;
  ticketCount: number;
  isAvailable: boolean;
}

export const ServiceDatePicker: FC<ServiceDatePickerProps> = ({
  value,
  onChange,
  disabled,
  className,
  serviceType,
}) => {
  // Check if this is a warehouse or fabrication service
  const isWarehouseOrFab = serviceType === ServiceType.WAREHOUSE_SERVICE || serviceType === ServiceType.FABRICATION_SERVICE;

  // Calculate initial view date - if we're past day 20, show next month so calendar isn't mostly empty
  const getInitialViewDate = () => {
    if (value) return value;
    const today = dayjs();
    return today.date() > 20 ? today.add(1, "month").startOf("month") : today;
  };

  // Calculate date range for fetching (current month +/- 1 month)
  const [viewDate, setViewDate] = useState<Dayjs>(getInitialViewDate());

  // Sync viewDate when value changes (e.g., from DatePicker)
  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setViewDate(date);
    }
    onChange?.(date);
  };

  const startDate = viewDate.startOf("month").subtract(7, "day").format("YYYY-MM-DD");
  const endDate = viewDate.endOf("month").add(7, "day").format("YYYY-MM-DD");

  // Fetch availability schedules for the visible range
  // NOTE: Backend max pageSize is 500
  const { data: availabilityData } = useGetStaffAvailabilities(
    {
      startDate,
      endDate,
      page: 1,
      pageSize: 500,
    },
    true
  );

  // Fetch vessels
  const { data: vessels } = useGetVesselsForDropdown();
  const activeVessels = useMemo(() => {
    return vessels?.filter((v) => v.isActive !== false) || [];
  }, [vessels]);
  const inactiveVessels = useMemo(() => {
    return vessels?.filter((v) => v.isActive === false) || [];
  }, [vessels]);

  // Fetch service tickets for the date range to calculate vessel workload
  const { data: serviceTicketsData } = useGetServiceTickets(
    {
      startDate,
      endDate,
      page: 1,
      pageSize: 500,
    },
    true
  );

  // Calculate vessel workload per day
  const vesselWorkloadMap = useMemo(() => {
    const map: Record<string, Record<string, VesselDayStats>> = {}; // date -> vesselId -> stats

    // Initialize all active vessels for each date
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      const dateStr = currentDate.format("YYYY-MM-DD");
      map[dateStr] = {};

      activeVessels.forEach((vessel) => {
        map[dateStr][vessel.id] = {
          vesselId: vessel.id,
          vesselName: vessel.name,
          status: vessel.status,
          totalHours: 0,
          ticketCount: 0,
          isAvailable: true,
        };
      });

      currentDate = currentDate.add(1, "day");
    }

    // Aggregate service ticket hours by vessel and date
    serviceTicketsData?.items?.forEach((ticket) => {
      if (!ticket.vesselId || !ticket.serviceDatetime) return;
      if (ticket.status === JobStatus.CANCELLED) return;

      const dateStr = dayjs(ticket.serviceDatetime).format("YYYY-MM-DD");
      if (!map[dateStr] || !map[dateStr][ticket.vesselId]) return;

      const duration = Number(ticket.duration) || 0;
      const travelTime = Number(ticket.travelTime) || 0;
      const totalHours = duration + travelTime;

      map[dateStr][ticket.vesselId].totalHours += totalHours;
      map[dateStr][ticket.vesselId].ticketCount += 1;

      // Mark as unavailable if > 8 hours booked
      if (map[dateStr][ticket.vesselId].totalHours >= 8) {
        map[dateStr][ticket.vesselId].isAvailable = false;
      }
    });

    return map;
  }, [activeVessels, serviceTicketsData?.items, startDate, endDate]);

  // Get vessel stats for a specific date
  const getVesselStatsForDate = (dateStr: string): VesselDayStats[] => {
    const dayStats = vesselWorkloadMap[dateStr];
    if (!dayStats) return [];

    return Object.values(dayStats).sort((a, b) => a.totalHours - b.totalHours);
  };

  // Calculate stats per day - directly from staff_availability_schedule entries
  const dayStatsMap = useMemo(() => {
    const map: Record<string, DayStats> = {};

    // Initialize all dates in range with empty stats
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      const dateStr = currentDate.format("YYYY-MM-DD");
      map[dateStr] = {
        date: dateStr,
        availableCount: 0,
        partTimeCount: 0,
        bookedCount: 0,
        unavailableCount: 0,
        totalWithSchedule: 0,
        availableDrivers: 0,
        availableLeads: 0,
      };
      currentDate = currentDate.add(1, "day");
    }

    // If no availability data, return the initialized map
    if (!availabilityData?.items?.length) return map;

    // Group schedules by date and count - use schedule.staff directly
    // Custom logic for this picker: categorize by availableHours
    availabilityData.items.forEach((schedule) => {
      const dateStr = dayjs(schedule.date).format("YYYY-MM-DD");
      if (!map[dateStr]) return; // Skip if outside our date range

      // Skip unavailable staff entirely
      if (schedule.availability === StaffAvailability.UNAVAILABLE) {
        map[dateStr].unavailableCount++;
        map[dateStr].totalWithSchedule++;
        return;
      }

      map[dateStr].totalWithSchedule++;

      // Categorize based on availableHours
      const availableHours = schedule.availableHours ?? 8;

      if (availableHours <= 0) {
        // Fully booked - no hours left
        map[dateStr].bookedCount++;
      } else if (availableHours < 8) {
        // Partially available
        map[dateStr].partTimeCount++;
        if (schedule.staff?.driver) map[dateStr].availableDrivers++;
        if (schedule.staff?.lead) map[dateStr].availableLeads++;
      } else {
        // Full availability (8+ hours)
        map[dateStr].availableCount++;
        if (schedule.staff?.driver) map[dateStr].availableDrivers++;
        if (schedule.staff?.lead) map[dateStr].availableLeads++;
      }
    });

    return map;
  }, [availabilityData?.items, startDate, endDate]);

  // Get color based on available staff count
  const getDateColor = (stats: DayStats | undefined): string => {
    if (!stats) return "";

    const totalAvailable = stats.availableCount + stats.partTimeCount;
    if (totalAvailable >= 6) return "bg-green-100 hover:bg-green-200";
    if (totalAvailable >= 3) return "bg-yellow-100 hover:bg-yellow-200";
    if (totalAvailable > 0) return "bg-orange-100 hover:bg-orange-200";
    return "bg-red-100 hover:bg-red-200";
  };

  // Get vessel workload color
  const getVesselWorkloadColor = (hours: number): string => {
    if (hours === 0) return "green";
    if (hours < 4) return "blue";
    if (hours < 6) return "orange";
    return "red";
  };

  // Custom cell render for calendar
  const cellRender = (current: Dayjs) => {
    const dateStr = current.format("YYYY-MM-DD");
    const stats = dayStatsMap[dateStr];
    const vesselStats = getVesselStatsForDate(dateStr);
    const isToday = current.isSame(dayjs(), "day");
    const isPast = current.isBefore(dayjs(), "day");
    const isSelected = value && current.isSame(value, "day");

    const colorClass = !isPast && stats ? getDateColor(stats) : "";
    const totalAvailable = stats ? stats.availableCount + stats.partTimeCount : 0;
    const availableVessels = vesselStats.filter((v) => v.isAvailable).length;

    return (
      <Tooltip
        title={
          !isPast && stats ? (
            <div className="text-xs">
              <div>{totalAvailable} staff, {availableVessels}v</div>
            </div>
          ) : null
        }
      >
        <div
          className={`h-full w-full flex flex-col items-center justify-center rounded cursor-pointer text-xs
            ${colorClass}
            ${isToday ? "ring-1 ring-blue-500" : ""}
            ${isSelected ? "ring-2 ring-blue-600 !bg-blue-500 text-white" : ""}
            ${isPast ? "text-gray-300" : ""}
          `}
        >
          {current.date()}
        </div>
      </Tooltip>
    );
  };

  // Get summary for selected date
  const selectedStats = value ? dayStatsMap[value.format("YYYY-MM-DD")] : null;
  const selectedVesselStats = value ? getVesselStatsForDate(value.format("YYYY-MM-DD")) : [];
  const availableVesselsCount = selectedVesselStats.filter((v) => v.isAvailable).length;
  const totalSelectedHours = selectedVesselStats.reduce((sum, v) => sum + v.totalHours, 0);

  // Calculate busy time slots per vessel for the selected date (8am-6pm = 10 hours)
  const vesselTimeSlots = useMemo(() => {
    if (!value || !serviceTicketsData?.items) return null;

    const selectedDateStr = value.format("YYYY-MM-DD");
    const selectedDate = value.startOf("day");
    const dayTickets = serviceTicketsData.items.filter((ticket) => {
      if (ticket.status === JobStatus.CANCELLED) return false;
      if (!ticket.serviceDatetime) return false;
      return dayjs(ticket.serviceDatetime).format("YYYY-MM-DD") === selectedDateStr;
    });

    // Group tickets by vessel
    const vesselMap: Record<string, {
      vesselId: string;
      vesselName: string;
      slots: number[];
      ticketCount: number;
      isInactive: boolean;
    }> = {};

    // Initialize active vessels that might be available even without tickets
    activeVessels.forEach((vessel) => {
      vesselMap[vessel.id] = {
        vesselId: vessel.id,
        vesselName: vessel.name,
        slots: new Array(10).fill(0),
        ticketCount: 0,
        isInactive: false,
      };
    });

    // Add inactive vessels - but only block if selected date is on or after deactivatedAt
    inactiveVessels.forEach((vessel) => {
      const deactivatedAt = vessel.deactivatedAt ? dayjs(vessel.deactivatedAt).startOf("day") : null;
      // Only show as inactive if the selected date is on or after deactivation date
      const isInactiveForDate = deactivatedAt ? selectedDate.isSame(deactivatedAt) || selectedDate.isAfter(deactivatedAt) : true;

      vesselMap[vessel.id] = {
        vesselId: vessel.id,
        vesselName: vessel.name,
        slots: isInactiveForDate ? new Array(10).fill(1) : new Array(10).fill(0), // Block only if inactive for this date
        ticketCount: 0,
        isInactive: isInactiveForDate,
      };
    });

    // Fill in slots from tickets
    dayTickets.forEach((ticket) => {
      if (!ticket.vesselId) return;

      // Ensure vessel exists in map
      if (!vesselMap[ticket.vesselId]) {
        vesselMap[ticket.vesselId] = {
          vesselId: ticket.vesselId,
          vesselName: ticket.vessel?.name || "Unknown",
          slots: new Array(10).fill(0),
          ticketCount: 0,
          isInactive: false,
        };
      }

      vesselMap[ticket.vesselId].ticketCount++;

      // Use startDatetime if available, otherwise serviceDatetime
      const timeSource = ticket.startDatetime || ticket.serviceDatetime;
      if (!timeSource) return;

      const startTime = dayjs(timeSource);
      const startHour = startTime.hour();
      const duration = Number(ticket.duration) || 1;

      // Fill in the slots this ticket occupies
      for (let h = 0; h < duration; h++) {
        const slotIndex = startHour + h - 8; // 8am = index 0
        if (slotIndex >= 0 && slotIndex < 10) {
          vesselMap[ticket.vesselId].slots[slotIndex]++;
        }
      }
    });

    // Convert to array - active vessels first, then inactive
    const vesselList = Object.values(vesselMap)
      .filter((v) => v.ticketCount > 0 || activeVessels.some((av) => av.id === v.vesselId) || v.isInactive)
      .sort((a, b) => {
        // Inactive vessels at the bottom
        if (a.isInactive !== b.isInactive) return a.isInactive ? 1 : -1;
        // Then by ticket count
        return b.ticketCount - a.ticketCount;
      });

    return {
      vessels: vesselList,
      totalTickets: dayTickets.length,
    };
  }, [value, serviceTicketsData?.items, activeVessels, inactiveVessels]);

  // Calculate warehouse/fabrication hours for the selected date
  const warehouseFabStats = useMemo(() => {
    if (!value || !serviceTicketsData?.items || !isWarehouseOrFab) return null;

    const selectedDateStr = value.format("YYYY-MM-DD");

    // Filter for warehouse or fabrication services on this date
    const relevantTickets = serviceTicketsData.items.filter((ticket) => {
      if (ticket.status === JobStatus.CANCELLED) return false;
      if (!ticket.serviceDatetime) return false;
      if (dayjs(ticket.serviceDatetime).format("YYYY-MM-DD") !== selectedDateStr) return false;

      // Match the service type
      if (serviceType === ServiceType.WAREHOUSE_SERVICE) {
        return ticket.serviceType === ServiceType.WAREHOUSE_SERVICE;
      }
      if (serviceType === ServiceType.FABRICATION_SERVICE) {
        return ticket.serviceType === ServiceType.FABRICATION_SERVICE;
      }
      return false;
    });

    const totalHours = relevantTickets.reduce((sum, ticket) => {
      return sum + (Number(ticket.duration) || 0);
    }, 0);

    return {
      ticketCount: relevantTickets.length,
      totalHours,
      label: serviceType === ServiceType.WAREHOUSE_SERVICE ? "Warehouse" : "Fabrication",
    };
  }, [value, serviceTicketsData?.items, isWarehouseOrFab, serviceType]);

  return (
    <div className="flex gap-3 border rounded-lg bg-gray-50 p-3">
      {/* Left Side - DatePicker + Info */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* DatePicker Dropdown */}
        <DatePicker
          value={value}
          onChange={handleDateChange}
          disabled={disabled}
          className="w-full"
          format="MMM D, YYYY"
          allowClear={false}
          disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
        />

        {/* Staff Summary */}
        {selectedStats && (
          <div className="p-2 bg-white rounded border text-xs">
            <div className="flex items-center gap-1 mb-1 font-medium text-gray-700">
              <Users size={12} />
              Staff Availability
            </div>
            {selectedStats.totalWithSchedule > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-1 text-center">
                  <span className="text-green-600">Open: {selectedStats.availableCount}</span>
                  <span className="text-yellow-600">Part: {selectedStats.partTimeCount}</span>
                  <span className="text-red-500">Booked: {selectedStats.bookedCount}</span>
                </div>
                <div className="mt-1 pt-1 border-t text-green-700 font-medium">
                  {selectedStats.availableCount + selectedStats.partTimeCount} available
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">No schedules submitted</div>
            )}
          </div>
        )}

        {/* Vessel Summary - only for non-warehouse/fab service types */}
        {!isWarehouseOrFab && selectedVesselStats.length > 0 && (
          <div className="p-2 bg-cyan-50 rounded border border-cyan-200 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="flex items-center gap-1 font-medium text-cyan-800">
                <Truck size={12} />
                Vessels
              </span>
              <span className="text-cyan-700 font-bold">{totalSelectedHours.toFixed(1)}h</span>
            </div>
            <div className="space-y-1 max-h-20 overflow-y-auto">
              {selectedVesselStats.map((vs) => (
                <div key={vs.vesselId} className="flex items-center justify-between">
                  <span className={vs.isAvailable ? "text-cyan-700" : "text-gray-400 line-through"}>
                    {vs.vesselName}
                  </span>
                  <span className={vs.totalHours >= 8 ? "text-red-500" : "text-gray-500"}>
                    {vs.totalHours.toFixed(1)}h
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-1 pt-1 border-t border-cyan-200 text-cyan-700">
              {availableVesselsCount}/{selectedVesselStats.length} available
            </div>
          </div>
        )}

        {/* Warehouse/Fabrication Summary - only for those service types */}
        {isWarehouseOrFab && warehouseFabStats && (
          <div className="p-2 bg-purple-50 rounded border border-purple-200 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-purple-700 font-medium">{warehouseFabStats.label} Load</span>
              <span className="text-purple-600 font-bold">{warehouseFabStats.totalHours}h</span>
            </div>
            <div className="text-purple-500 text-[10px] mt-1">
              {warehouseFabStats.ticketCount} service{warehouseFabStats.ticketCount !== 1 ? 's' : ''} scheduled
            </div>
          </div>
        )}

        {/* Vessel Time Slots - only for non-warehouse/fab service types */}
        {!isWarehouseOrFab && vesselTimeSlots && vesselTimeSlots.vessels.length > 0 && (
          <div className="p-2 bg-white rounded border text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600 font-medium">Day Schedule</span>
              <span className="text-gray-400">{vesselTimeSlots.totalTickets} services</span>
            </div>
            {/* Vessel rows */}
            <div className="space-y-1">
              {vesselTimeSlots.vessels.map((vessel) => (
                <div key={vessel.vesselId} className="flex items-center gap-1">
                  {/* Vessel name */}
                  <div
                    className={`w-12 truncate text-[10px] ${vessel.isInactive ? 'text-red-400 line-through' : 'text-gray-500'}`}
                    title={vessel.isInactive ? `${vessel.vesselName} (Inactive)` : vessel.vesselName}
                  >
                    {vessel.vesselName}
                  </div>
                  {/* Timeline bar */}
                  <div className={`flex-1 flex h-3 rounded overflow-hidden border ${vessel.isInactive ? 'border-red-300' : ''}`}>
                    {vessel.isInactive ? (
                      // Inactive vessel - show fully blocked with red striped pattern
                      <div className="flex-1 bg-red-200 flex items-center justify-center">
                        <span className="text-[8px] text-red-500 font-medium">INACTIVE</span>
                      </div>
                    ) : (
                      vessel.slots.map((count, index) => {
                        const hour = index + 8;
                        const label = hour <= 12 ? `${hour}${hour === 12 ? 'pm' : 'am'}` : `${hour - 12}pm`;
                        // Simple: green if open, shaded if booked
                        const bgColor = count === 0 ? "bg-green-100" : "bg-gray-400";
                        return (
                          <Tooltip key={index} title={`${vessel.vesselName} @ ${label}: ${count === 0 ? 'open' : 'booked'}`}>
                            <div
                              className={`flex-1 ${bgColor} border-r border-gray-200 last:border-r-0 cursor-pointer hover:opacity-80`}
                            />
                          </Tooltip>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Time labels */}
            <div className="flex mt-1 text-[9px] text-gray-400">
              <div className="w-12" />
              <div className="flex-1 flex justify-between">
                <span>8am</span>
                <span>12pm</span>
                <span>6pm</span>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Legend */}
        <div className="text-[10px] text-gray-400 italic">
          <span className="text-green-500">●</span> 6+ staff{" "}
          <span className="text-yellow-500">●</span> 3-5{" "}
          <span className="text-orange-500">●</span> 1-2{" "}
          <span className="text-red-500">●</span> 0
        </div>
      </div>

      {/* Right Side - Mini Calendar */}
      <div className="w-[220px] flex-shrink-0">
        <Calendar
          fullscreen={false}
          value={value || viewDate}
          onSelect={(date) => {
            if (!date.isBefore(dayjs(), "day")) {
              handleDateChange(date);
            }
          }}
          onPanelChange={(date) => setViewDate(date)}
          fullCellRender={cellRender}
          headerRender={({ value: headerValue, onChange: headerOnChange }) => (
            <div className="flex items-center justify-between px-2 py-1 border-b">
              <button
                type="button"
                onClick={() => {
                  const newDate = headerValue.subtract(1, "month");
                  setViewDate(newDate);
                  headerOnChange(newDate);
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                ‹
              </button>
              <span className="text-sm font-medium">
                {headerValue.format("MMM YYYY")}
              </span>
              <button
                type="button"
                onClick={() => {
                  const newDate = headerValue.add(1, "month");
                  setViewDate(newDate);
                  headerOnChange(newDate);
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                ›
              </button>
            </div>
          )}
          className="compact-calendar bg-white rounded border"
        />
      </div>
    </div>
  );
};
